import { imagePathResolver } from '@shared/utils';
import Image from 'next/image';
import { ArticleAuthor } from '../ArticleAuthor/ArticleAuthor';
import { ArticleContent } from '../ArticleContent/ArticleContent';
import { ArticleHeading } from '../ArticleHeading/ArticleHeading';
import { ArticlePagination } from '../ArticlePagination/ArticlePagination';
import { AuthorProfile } from '../AuthorProfile/AuthorProfile';
import { ArticleBreadcrumbList } from '../Breadcrumbs/ArticleBreadcrumbList';
import type { MagazineArticle } from '../helpers/magazineCategory';
import { MagazineTrackMeta } from '../MagazineBody/MagazineTrackMeta';

type MagazineArticlePageProps = {
  article: MagazineArticle;
  baseUrl: string;
  baseImgUrl: string;
};

function MagazineArticlePage({ article, baseUrl, baseImgUrl }: MagazineArticlePageProps) {
  const authorSlug = article.author.slug;
  const featuredImageFilePath = article.featuredImage?.filePath;
  const featuredImageSrc = featuredImageFilePath ? imagePathResolver(featuredImageFilePath, baseImgUrl) : '';
  const authorImageSrc = article.author.profilePicture?.filePath ? imagePathResolver(article.author.profilePicture.filePath, baseImgUrl) : undefined;

  return (
    <div data-testid="magazine-article-page">
      <MagazineTrackMeta container="Article" type={article.category?.title} title={article.title} provider={article.author.name} />
      <ArticleBreadcrumbList slug={article.slug} title={article.title} category={article.category} baseUrl={baseUrl} />
      <ArticleHeading title={article.title} summary={article.summary} publishedDate={article.publishedDate} category={article.category} />
      {featuredImageSrc && (
        <div className="relative mt-8 h-[200px] w-full overflow-hidden rounded-lg" data-testid="magazine-article-featured-image">
          <Image src={featuredImageSrc} alt={article.featuredImage?.altText ?? ''} fill className="object-cover" priority sizes="(min-width: 1024px) 66vw, 100vw" />
        </div>
      )}
      <ArticleContent content={article.content} className="mt-8" />
      <ArticleAuthor name={article.author.name} authorSlug={authorSlug} className="mt-8" />
      <ArticlePagination previousArticle={article.previousArticle} nextArticle={article.nextArticle} className="mt-8" />
      <AuthorProfile name={article.author.name} authorSlug={authorSlug} bio={article.author.bio} imageSrc={authorImageSrc} className="mt-8" />
    </div>
  );
}

export { MagazineArticlePage, type MagazineArticlePageProps };
